import {
  useEffect,
  useRef,
  useState,
  type FormEvent,
  type KeyboardEvent,
} from 'react';
import { Button } from '@/components/ui/Button';
import { cn } from '@/lib/utils/cn';

const MAX_CHARS = 500;
const SOFT_LIMIT_HINT = 400;

type Props = {
  isStreaming: boolean;
  onSubmit: (question: string) => void;
  onCancel: () => void;
};

/**
 * Ask input. Multi-line auto-expanding textarea. ⌘↩ / Ctrl↩ submits.
 * Plain Enter inserts a newline so the user can compose carefully.
 * Submit disabled when empty or while streaming; Cancel replaces Submit
 * while streaming.
 */
export function AskBox({ isStreaming, onSubmit, onCancel }: Props) {
  const [draft, setDraft] = useState('');
  const ref = useRef<HTMLTextAreaElement | null>(null);

  // Auto-grow textarea up to ~6 rows.
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    el.style.height = 'auto';
    el.style.height = `${Math.min(el.scrollHeight, 200)}px`;
  }, [draft]);

  function commit(e?: FormEvent) {
    e?.preventDefault();
    const q = draft.trim();
    if (!q || isStreaming) return;
    onSubmit(q);
  }

  function handleKeyDown(e: KeyboardEvent<HTMLTextAreaElement>) {
    // Cmd/Ctrl + Enter submits.
    if ((e.metaKey || e.ctrlKey) && e.key === 'Enter') {
      e.preventDefault();
      commit();
    }
  }

  const charCount = draft.length;
  const showCounter = charCount > SOFT_LIMIT_HINT;
  const overLimit = charCount > MAX_CHARS;

  return (
    <form onSubmit={commit} className="relative">
      <label className="block">
        <span className="sr-only">Your question</span>
        <textarea
          ref={ref}
          value={draft}
          onChange={(e) =>
            setDraft(e.target.value.slice(0, MAX_CHARS + 50))
          }
          onKeyDown={handleKeyDown}
          placeholder="What does the Quran say about…"
          rows={1}
          disabled={isStreaming}
          className={cn(
            'w-full resize-none overflow-hidden',
            'min-h-[3.5rem] px-4 py-4 pr-32',
            'bg-white dark:bg-ink-900',
            'border rounded-2xl',
            overLimit
              ? 'border-red-300 dark:border-red-700'
              : 'border-ink-200 dark:border-ink-800',
            'text-base sm:text-lg text-ink-900 dark:text-ink-50',
            'placeholder:text-ink-400 dark:placeholder:text-ink-500',
            'focus:border-accent focus:outline-none focus:ring-2 focus:ring-accent/40',
            'disabled:opacity-60',
            'transition-colors',
          )}
        />
      </label>

      <div className="absolute right-3 bottom-3 flex items-center gap-2">
        {showCounter && (
          <span
            className={cn(
              'text-xs num-tight',
              overLimit ? 'text-red-600 dark:text-red-400' : 'text-ink-400',
            )}
          >
            {charCount}/{MAX_CHARS}
          </span>
        )}
        {isStreaming ? (
          <Button
            type="button"
            size="sm"
            variant="secondary"
            onClick={onCancel}
          >
            Stop
          </Button>
        ) : (
          <Button
            type="submit"
            size="sm"
            disabled={!draft.trim() || overLimit}
          >
            Ask
          </Button>
        )}
      </div>

      <p className="mt-2 text-[11px] text-ink-400 dark:text-ink-500">
        <kbd className="font-sans">⌘↩</kbd> or{' '}
        <kbd className="font-sans">Ctrl↩</kbd> to send
      </p>
    </form>
  );
}
